import _ from 'lodash'

import { ApplicationContext } from '../applicationContext'
import { JSONSchemaResolver, RefResolver } from '../../../resolvers/json-schema-resolver'

const MAX_DEPTH = 8

interface EntitySpec {
  mixins?: string[]
}

/**
 * Decides whether a property is emitted as a path. Receives the property's own schema node and
 * its full path parts (namespace-first, e.g. `['carrier', 'address', 'street']`). Gates EMISSION
 * only — the walk still descends into a filtered-out object's children, so a labelled leaf under an
 * unlabelled parent is still reachable (mirrors the AI-block picker's `getOptionsFromSchemaDeep`,
 * which recurses into every object/$ref but only surfaces options with a non-empty label).
 */
export type SchemaPathFilter = (propertySchema: any, pathParts: string[]) => boolean

const INCLUDE_ALL: SchemaPathFilter = () => true

/**
 * POSSIBLE_PATHS — given the `entities` dictionary of a multi-entity ingestion payload config
 * (alias -> { mixins: [schemaUri, ...] }), produce candidate munging target paths in the form
 * `<alias>.<namespace>.<property>` (e.g. `carrier.carrier.scac`, `appt.core_calendar_appointment.refNumber`).
 *
 * Feeds autocomplete suggestions for the Property Map's Target Path key. Reads schemas from the store
 * CACHE ONLY (`getRecord`) — no remote fetch — since the Entities section on the same page already loads
 * each alias's mixin schemas. Synchronous and best-effort: a mixin whose schema isn't cached (or a
 * `$ref` whose target isn't cached) just yields fewer suggestions; the field stays free-form.
 */
export function possiblePaths(
  entities: Record<string, EntitySpec> | { toJS?: () => any } | null | undefined
): string[] {
  const store = (this as ApplicationContext)?.getStore()
  const plain = normalizeEntities(entities)
  if (!store || typeof store.getRecord !== 'function' || _.isEmpty(plain)) {
    return []
  }

  // Cache-only $ref resolver: JSONSchemaResolver.resolveReference is synchronous and reads solely from
  // store.getRecord (the remote-fetching path is resolveRemoteSchema, which we never call).
  const resolver = new JSONSchemaResolver({ getStore: () => store })

  const paths: string[] = []
  for (const alias of Object.keys(plain)) {
    const mixins = _.get(plain[alias], 'mixins', [])
    if (!Array.isArray(mixins)) {
      continue
    }
    for (const uri of mixins) {
      if (!isSchemaUri(uri)) {
        continue
      }
      const schema = store.getRecord(uri)
      if (!schema) {
        continue
      }
      // Enumerate every candidate (no label filter — the payload target field stays free-form), then
      // stamp this alias onto each namespace-relative path.
      paths.push(...getPathsFromSchemaDeep(schema, INCLUDE_ALL, resolver).map((path) => `${alias}.${path}`))
    }
  }
  return _.uniq(paths).sort()
}

/**
 * ENTITY_TYPE_URIS — the metadata URIs of the schemas currently in the client store, sorted. Feeds an
 * autocomplete for entity-type / mixin pickers so URIs are selectable instead of hand-typed. The store
 * keys its schema map by URI (`getSchemas()`), so this is just its keys — no per-schema traversal.
 * Synchronous and reflects only schemas already loaded, so the field it feeds should stay free-form
 * (creatable) for anything missing.
 */
export function entityTypeUris(): string[] {
  const store = (this as ApplicationContext)?.getStore()
  if (!store || typeof store.getSchemas !== 'function') {
    return []
  }
  return Object.keys(store.getSchemas() || {}).sort()
}

function normalizeEntities(entities: any): Record<string, EntitySpec> {
  if (!entities) {
    return {}
  }
  const plain = typeof entities.toJS === 'function' ? entities.toJS() : entities
  return _.isPlainObject(plain) ? plain : {}
}

/*
 * A mixin is a schema metadata URI (e.g. /1.0/entities/metadata/core_yms_spot.json). While the config is
 * being edited a mixin can be blank or half-typed; skip anything that isn't a well-formed URI so a
 * partial value doesn't produce a bogus lookup.
 */
function isSchemaUri(uri: any): uri is string {
  return typeof uri === 'string' && uri.startsWith('/') && uri.endsWith('.json')
}

/*
 * Walk every top-level namespace block of a materialized entity-type schema. A
 * type schema resolves its mixins into one property block per namespace (e.g.
 * carrierAppointment yields both `core_calendar_appointment` and
 * `core_yms_carrierAppointment`), so this reproduces the `<namespace>.<property>`
 * paths the server accepts. The bare namespace itself is not emitted — only
 * paths one level or deeper, which is where munging targets actually live.
 *
 * `filter` decides which properties are emitted (default: all). Pass e.g.
 * `(prop) => !_.isEmpty(prop.label)` for the AI-block-style "labelled fields only" use.
 *
 * `resolver` (optional) follows property `$ref`s so nested definitions (address, fileSet, edges…)
 * contribute their inner paths too. It must be synchronous and cache-backed — pass a JSONSchemaResolver
 * and only its store-only `resolveReference` is used; unresolvable refs are skipped (best-effort).
 */
export function getPathsFromSchemaDeep(
  entitySchema: any,
  filter: SchemaPathFilter = INCLUDE_ALL,
  resolver?: RefResolver
): string[] {
  const properties = entitySchema?.get?.('properties') ?? entitySchema?.properties
  const out: string[] = []
  _.forEach(properties, (namespaceSchema: any, namespace: string) => {
    const namespaceProps = _.get(namespaceSchema, 'properties')
    if (namespaceProps) {
      out.push(...walkProperties(namespaceProps, [namespace], 1, filter, resolver, entitySchema, new Set()))
    }
  })
  return out
}

function walkProperties(
  properties: any,
  prefix: string[],
  depth: number,
  filter: SchemaPathFilter,
  resolver: RefResolver | undefined,
  context: any,
  seenRefs: Set<string>
): string[] {
  if (!properties || depth > MAX_DEPTH) {
    return []
  }
  const out: string[] = []
  _.forEach(properties, (propertySchema: any, key: string) => {
    if (!_.isObject(propertySchema)) {
      return
    }
    const prop = propertySchema as any
    const path = [...prefix, key]
    if (filter(propertySchema, path)) {
      out.push(path.join('.'))
    }
    // Inline object / array-of-objects.
    if (prop.properties) {
      out.push(...walkProperties(prop.properties, path, depth + 1, filter, resolver, context, seenRefs))
    }
    if (prop.items?.properties) {
      out.push(...walkProperties(prop.items.properties, path, depth + 1, filter, resolver, context, seenRefs))
    }
    // $ref (on the property itself or on array items) — resolve against the store and descend. seenRefs
    // breaks cycles from self-referential definitions; MAX_DEPTH still bounds overall depth.
    const ref: string | undefined = prop.$ref ?? prop.items?.$ref
    if (ref && resolver && !seenRefs.has(ref)) {
      const resolved = resolveRefProperties(resolver, context, ref)
      if (resolved) {
        out.push(
          ...walkProperties(
            resolved.properties,
            path,
            depth + 1,
            filter,
            resolver,
            resolved.context,
            new Set([...seenRefs, ref])
          )
        )
      }
    }
  })
  return out
}

/*
 * Resolve a `$ref` to the property block it introduces, cache-only and best-effort. Handles both a
 * definition/entity object (`properties`) and an array definition (`items.properties`), and both a
 * materialized store record (`.get`) and a plain fragment object. Returns undefined — skipping the
 * ref — when it can't be resolved from the store or carries no nested properties.
 */
function resolveRefProperties(
  resolver: RefResolver,
  context: any,
  ref: string
): { properties: any; context: any } | undefined {
  try {
    const { schema, context: nextContext } = resolver.resolveReference(context, ref)
    const properties =
      schema?.get?.('properties') ?? _.get(schema, 'properties') ?? _.get(schema, 'items.properties')
    if (properties) {
      return { properties, context: nextContext ?? context }
    }
  } catch {
    // ref not in the store cache (or otherwise unresolvable) — yield fewer suggestions, stay free-form.
  }
  return undefined
}
